<template>
  <div class="washer-map">
    <Map.OlMap
      v-if="mapLoaded"
      style="width: 100%; height: 500px"
      :load-tiles-while-animating="true"
      :load-tiles-while-interacting="true"
      data-cy="washermap-page"
    >
      <Map.OlView
        :center="mapCenter"
        :zoom="mapZoom"
        :projection="'EPSG:3857'"
      />

      <Layers.OlTileLayer v-bind="{} as any">
        <Sources.OlSourceOsm />
      </Layers.OlTileLayer>

      <Interactions.OlInteractionSelect
        :filter="selectTrailerFilter"
        @select="onSelect($event, 'trailer')"
      >
        <Styles.OlStyle>
          <Styles.OlStyleIcon :src="trailerIcon" :width="36" :height="36" />
        </Styles.OlStyle>
      </Interactions.OlInteractionSelect>

      <Interactions.OlInteractionSelect
        :filter="selectRailcarFilter"
        @select="onSelect($event, 'railcar')"
      >
        <Styles.OlStyle>
          <Styles.OlStyleIcon :src="railcarIcon" :width="36" :height="36" />
        </Styles.OlStyle>
      </Interactions.OlInteractionSelect>

      <Layers.OlVectorLayer v-bind="{} as any">
        <Sources.OlSourceVector :format="geoJson" :features="trailerFeatures" />
        <Styles.OlStyle>
          <Styles.OlStyleIcon :src="trailerIcon" :width="32" :height="32" />
        </Styles.OlStyle>
      </Layers.OlVectorLayer>

      <Layers.OlVectorLayer v-bind="{} as any">
        <Sources.OlSourceVector :features="railcarFeatures" />
        <Styles.OlStyle>
          <Styles.OlStyleIcon :src="railcarIcon" :width="32" :height="32" />
        </Styles.OlStyle>
      </Layers.OlVectorLayer>

      <Map.OlOverlay
        v-if="selectedFeature"
        :position="(selectedFeature!.getGeometry() as any).getCoordinates()"
      >
        <template v-slot>
          <div class="washer-popup">
            <div v-html="selectedFeature.get('popup')"></div>
          </div>
        </template>
      </Map.OlOverlay>
    </Map.OlMap>

    <div style="padding: 0px 20px 1px; margin-top: 0">
      <div style="position: relative; margin: 0 -20px">
        <div class="washer-legend">
          <div class="washer-legend-element">
            <img src="@/assets/img/railcar.png" alt="Railcar" />
            <span>Railcar Washing facilities</span>
          </div>
          <div class="washer-legend-element">
            <img src="@/assets/img/trailer.png" alt="Trailer" />
            <span>Trailer Washing facilities</span>
          </div>
        </div>
      </div>
    </div>

    <div class="zip-search__container">
      <div class="col-sm-4">
        <input
          type="text"
          class="form-control zip-search__input"
          v-model="searchValue"
          placeholder="Enter zipcode, city or state to search nearest facilities"
        />
      </div>
      <Button :theme-color="'primary'" type="button" @click="search"
        >Search</Button
      >
      <Button :theme-color="'primary'" type="button" @click="resetMap"
        >Reset Map</Button
      >
    </div>

    <div>
      <br />
      <p style="font-size: 12px; color: #999999">
        For more information visit
        <a href="https://oukosher.org/wash-facilities/" target="_blank"
          >https://oukosher.org/wash-facilities/</a
        >
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount } from "vue";
import { Map, Layers, Sources, Styles, Interactions } from "vue3-openlayers";
import "vue3-openlayers/styles.css";
import { Button } from "@progress/kendo-vue-buttons";
import { api as osAPI } from "@/api/zipLookup";
import type { WasherFacilities } from "@/Domain/WasherFacilities";
import type { Location } from "@/Domain/Location";
import { GeoJSON } from "ol/format";
import { transform } from "ol/proj";
import type { Geometry } from "ol/geom";
import type Feature from "ol/Feature";

let defCenter = [-97, 40];
const defZoom = 5;
const mapCenter = ref(defCenter);
const mapZoom = ref(defZoom);
const mapLoaded = ref(false);
const searchValue = ref("");
const locations = ref<Location[]>([]);
const facilities = ref<WasherFacilities>();
const selectedFeature = ref<Feature<Geometry> | null>(null);

const geoJson = new GeoJSON();

const trailerIcon = new URL("@/assets/img/trailer.png", import.meta.url).href;
const railcarIcon = new URL("@/assets/img/railcar.png", import.meta.url).href;

const featuresFromMarkers = (markers: any[]) => {
  const features = markers.map((item) => ({
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: transformCoordinates(item.latlng[1], item.latlng[0]),
    },
    properties: {
      name: item.name,
      popup: item.popup,
      icon: item.icon,
    },
  }));

  const providerFeatureCollection = {
    type: "FeatureCollection",
    features,
  };

  return geoJson.readFeatures(providerFeatureCollection);
};

const markers = computed(() => {
  return (
    facilities.value?.results.map((item) => {
      return {
        latlng: [item.latitude, item.longitude],
        name: item.company,
        popup:
          `<strong>${item.company}</strong><br/>${item.address} ${item.city} ${item.state}<br/>${item.contact}<br/>${item.phone}<br/>` +
          `<a href="mailto:${item.email}">${item.email}</a><br/>` +
          `<a href="${item.website}" target="_blank">${item.website}</a><br/>`,
        icon: item.type,
      };
    }) || []
  );
});

const trailerFeatures = computed(() =>
  featuresFromMarkers(markers.value.filter((m) => m.icon === "trailer"))
);
const railcarFeatures = computed(() =>
  featuresFromMarkers(markers.value.filter((m) => m.icon === "railcar"))
);

onBeforeMount(async () => {
  const result = await osAPI.getWasherFacilitiesAsync();
  if (result.success) facilities.value = result.response;
  for (const item of facilities.value!.results) {
    if (!item.website) item.website = "";
  }
  mapLoaded.value = true;
});

const transformCoordinates = (lon: number, lat: number) =>
  transform([lon, lat], "EPSG:4326", "EPSG:3857");

const search = async () => {
  const result = await osAPI.get("Loc", {}, searchValue.value);
  if (result.success) locations.value = result.response;
  const location = locations.value[0];
  if (!location) return;
  mapCenter.value = transformCoordinates(location.lon, location.lat);
  mapZoom.value = 7;
};

const onSelect = (event: any, type: string) => {
  console.log(event);
  const feature = event.selected[0];
  if (!feature && selectedFeature.value?.get("icon") !== type) return;
  selectedFeature.value = feature;
};

const resetMap = () => {
  mapCenter.value = defCenter;
  mapZoom.value = defZoom;
};

const selectTrailerFilter = (feature: Feature<Geometry>) =>
  feature.get("icon") === "trailer";

const selectRailcarFilter = (feature: Feature<Geometry>) =>
  feature.get("icon") === "railcar";

onBeforeMount(() => {
  defCenter = transformCoordinates(defCenter[0], defCenter[1]);
  resetMap();
});
</script>

<style lang="scss">
.washer-map {
  margin: 10px 30px 30px;

  .washer-popup {
    padding: 10px;
    background: rgba(0, 0, 0, 0.8);
    border-radius: 4px;
    box-shadow: 0px 1px 1px rgba(70, 70, 70, 0.6);
    color: white;
  }
}
.washer-legend {
  padding: 12px 15px;
  font-size: 12px;
  border: 1px solid #ccc;
  position: absolute;
  bottom: 5px;
  background: white;
  border-radius: 3px;
  box-shadow: 0px 1px 1px rgba(70, 70, 70, 0.6);
}
.washer-legend-element {
  display: flex;
  align-items: center;
  gap: 5px;
}
.zip-search__container {
  margin: 20px 0 10px;
  display: flex;
  justify-content: center;
  gap: 8px;
}
.zip-search__input {
  width: 350px;
}
</style>
